import { type FormEvent, useCallback, useState } from 'react';
import { type PlatformUser, useAddOrgMember, useUsers } from '../../hooks/useAdminData';
import { Modal } from '../Modal';
import { SearchSelect } from '../SearchSelect';

interface AddMemberModalProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
}

export function AddMemberModal({ open, onClose, orgId }: AddMemberModalProps) {
  const [selectedUser, setSelectedUser] = useState<PlatformUser | null>(null);
  const [role, setRole] = useState('member');
  const [searchQuery, setSearchQuery] = useState('');

  const addMember = useAddOrgMember();
  const { data: usersData, isLoading } = useUsers(
    searchQuery ? { search: searchQuery, limit: 10 } : { limit: 10 },
  );

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!selectedUser) return;

    addMember.mutate(
      { orgId, userId: selectedUser.id, role },
      {
        onSuccess: () => {
          setSelectedUser(null);
          setRole('member');
          onClose();
        },
      },
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Add Member">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">User</label>
          <SearchSelect<PlatformUser>
            placeholder="Search users by name or email..."
            onSearch={handleSearch}
            options={usersData?.data ?? []}
            loading={isLoading}
            selected={selectedUser}
            onSelect={setSelectedUser}
            renderOption={(user) => (
              <div>
                <div className="text-text text-sm">{user.fullName}</div>
                <div className="text-text-muted text-xs">{user.email}</div>
              </div>
            )}
            renderSelected={(user) => (
              <span>
                {user.fullName} ({user.email})
              </span>
            )}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Role</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
            <option value="owner">Owner</option>
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!selectedUser || addMember.isPending}
            className="px-4 py-2 text-sm rounded-lg font-medium bg-primary text-background hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {addMember.isPending ? 'Adding...' : 'Add Member'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
